import type {RouteRecordRaw} from 'vue-router';

/**
 * **Paths are English, the interface is not.** The reader picks Dutch or English and the
 * catalogues follow, but a URL is not interface text: it is shared in tickets, typed by
 * developers and read in logs, and having it change with a language preference — or sit
 * in one language while the rest of the tool sits in another — helps nobody. English is
 * the dominant language here, so the paths are English and stay that way.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {public: true, titleKey: 'titles.login'},
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () => import('@/views/PasswordResetView.vue'),
    meta: {public: true, titleKey: 'titles.passwordReset'},
  },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      {path: '', redirect: {name: 'requests'}},
      {
        path: 'requests',
        name: 'requests',
        component: () => import('@/views/RequestsView.vue'),
        meta: {roles: ['ORG_ADMIN', 'ORG_MEMBER'], titleKey: 'titles.requests'},
      },
      /**
       * Before `requests/:id`, which would otherwise swallow them: `new` is a perfectly
       * good id as far as the matcher is concerned. `routes.spec.ts` holds that order.
       */
      {
        path: 'requests/new',
        name: 'new-request',
        component: () => import('@/views/NewRequestView.vue'),
        meta: {roles: ['ORG_ADMIN', 'ORG_MEMBER'], titleKey: 'titles.newRequest'},
      },
      {
        path: 'requests/new/profile',
        name: 'new-request-profile',
        component: () => import('@/views/NewRequestProfileView.vue'),
        meta: {roles: ['ORG_ADMIN', 'ORG_MEMBER'], titleKey: 'titles.newRequestProfile'},
      },
      {
        path: 'requests/:id',
        name: 'request',
        component: () => import('@/views/RequestDetailView.vue'),
        meta: {roles: ['ORG_ADMIN', 'ORG_MEMBER'], titleKey: 'titles.request'},
      },
      {
        path: 'organizations',
        name: 'organizations',
        component: () => import('@/views/OrganizationsView.vue'),
        meta: {roles: ['PLATFORM_ADMIN'], titleKey: 'titles.organizations'},
      },
      {
        path: 'congestion-areas',
        name: 'congestion-areas',
        component: () => import('@/views/CongestionAreasView.vue'),
        meta: {roles: ['PLATFORM_ADMIN'], titleKey: 'titles.congestionAreas'},
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: {roles: ['ORG_ADMIN'], titleKey: 'titles.settings'},
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/UsersView.vue'),
        meta: {roles: ['PLATFORM_ADMIN', 'ORG_ADMIN'], titleKey: 'titles.users'},
      },
      {
        path: 'audit-log',
        name: 'audit',
        component: () => import('@/views/AuditView.vue'),
        meta: {roles: ['PLATFORM_ADMIN', 'ORG_ADMIN'], titleKey: 'titles.audit'},
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: {titleKey: 'titles.profile'},
      },
    ],
  },
  {path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue')},
];
